//#region src/biquad.ts
function normalise(b0, b1, b2, a0, a1, a2) {
	return {
		b0: b0 / a0,
		b1: b1 / a0,
		b2: b2 / a0,
		a1: a1 / a0,
		a2: a2 / a0
	};
}
function omega(frequency, sampleRate) {
	return 2 * Math.PI * frequency / sampleRate;
}
/**
* Rejects a design that cannot be built.
*
* Unchecked, a cutoff at or above Nyquist returned a whole channel of NaN or
* ~1e38, and a cutoff or Q of zero made `prime()` compute 0/0 — silently, for
* every sample. `notch()` already refused an out-of-range centre; now every
* design does.
*/
function assertDesign(kind, frequency, sampleRate, q) {
	if (!(sampleRate > 0) || !Number.isFinite(sampleRate)) throw new RangeError(`${kind}: sample rate must be positive, got ${sampleRate}`);
	if (!(frequency > 0 && frequency < sampleRate / 2)) throw new RangeError(`${kind}: frequency must lie strictly between 0 and Nyquist (${sampleRate / 2} Hz), got ${frequency}`);
	if (!(q > 0) || !Number.isFinite(q)) throw new RangeError(`${kind}: Q must be positive, got ${q}`);
}
function lowpassCoefficients(frequency, sampleRate, q = Math.SQRT1_2) {
	assertDesign("low-pass", frequency, sampleRate, q);
	const w = omega(frequency, sampleRate);
	const cos = Math.cos(w);
	const alpha = Math.sin(w) / (2 * q);
	return normalise((1 - cos) / 2, 1 - cos, (1 - cos) / 2, 1 + alpha, -2 * cos, 1 - alpha);
}
function highpassCoefficients(frequency, sampleRate, q = Math.SQRT1_2) {
	assertDesign("high-pass", frequency, sampleRate, q);
	const w = omega(frequency, sampleRate);
	const cos = Math.cos(w);
	const alpha = Math.sin(w) / (2 * q);
	return normalise((1 + cos) / 2, -(1 + cos), (1 + cos) / 2, 1 + alpha, -2 * cos, 1 - alpha);
}
/**
* Band-stop centred on `frequency`.
*
* The default Q of 30 gives roughly a 2 Hz notch at 50 Hz — narrow enough to
* leave the surrounding gamma band largely intact, which a wider notch does not.
*/
function notchCoefficients(frequency, sampleRate, q = 30) {
	assertDesign("notch", frequency, sampleRate, q);
	const w = omega(frequency, sampleRate);
	const cos = Math.cos(w);
	const alpha = Math.sin(w) / (2 * q);
	return normalise(1, -2 * cos, 1, 1 + alpha, -2 * cos, 1 - alpha);
}
/** Applies one section in place-free form, carrying state across calls. */
var BiquadFilter = class {
	#c;
	#x1 = 0;
	#x2 = 0;
	#y1 = 0;
	#y2 = 0;
	constructor(coefficients) {
		this.#c = coefficients;
	}
	reset() {
		this.#x1 = 0;
		this.#x2 = 0;
		this.#y1 = 0;
		this.#y2 = 0;
	}
	/** Primes the state so a filter starts settled at `value` rather than at zero. */
	prime(value) {
		this.#x1 = value;
		this.#x2 = value;
		const { b0, b1, b2, a1, a2 } = this.#c;
		const steady = value * (b0 + b1 + b2) / (1 + a1 + a2);
		this.#y1 = steady;
		this.#y2 = steady;
	}
	step(x) {
		const { b0, b1, b2, a1, a2 } = this.#c;
		const y = b0 * x + b1 * this.#x1 + b2 * this.#x2 - a1 * this.#y1 - a2 * this.#y2;
		this.#x2 = this.#x1;
		this.#x1 = x;
		this.#y2 = this.#y1;
		this.#y1 = y;
		return y;
	}
	/** Magnitude response at one frequency, for verifying a design. */
	magnitudeAt(frequency, sampleRate) {
		const w = omega(frequency, sampleRate);
		const { b0, b1, b2, a1, a2 } = this.#c;
		const cos1 = Math.cos(w);
		const sin1 = Math.sin(w);
		const cos2 = Math.cos(2 * w);
		const sin2 = Math.sin(2 * w);
		const numeratorReal = b0 + b1 * cos1 + b2 * cos2;
		const numeratorImaginary = -(b1 * sin1 + b2 * sin2);
		const denominatorReal = 1 + a1 * cos1 + a2 * cos2;
		const denominatorImaginary = -(a1 * sin1 + a2 * sin2);
		return Math.hypot(numeratorReal, numeratorImaginary) / Math.hypot(denominatorReal, denominatorImaginary);
	}
};
//#endregion
//#region src/filters.ts
function applyOnce(signal, sections) {
	const out = Float32Array.from(signal);
	for (const coefficients of sections) {
		const filter = new BiquadFilter(coefficients);
		filter.prime(out[0] ?? 0);
		for (let i = 0; i < out.length; i++) out[i] = filter.step(out[i]);
	}
	return out;
}
function reversed(signal) {
	const out = new Float32Array(signal.length);
	for (let i = 0; i < signal.length; i++) out[i] = signal[signal.length - 1 - i];
	return out;
}
function apply(signal, sections, zeroPhase) {
	const forward = applyOnce(signal, sections);
	if (!zeroPhase) return forward;
	return reversed(applyOnce(reversed(forward), sections));
}
function lowpass(signal, cutoffHz, options) {
	return apply(signal, [lowpassCoefficients(cutoffHz, options.sampleRate)], options.zeroPhase ?? true);
}
function highpass(signal, cutoffHz, options) {
	return apply(signal, [highpassCoefficients(cutoffHz, options.sampleRate)], options.zeroPhase ?? true);
}
/** Band-pass, built as a high-pass followed by a low-pass. */
function bandpass(signal, lowHz, highHz, options) {
	return apply(signal, [highpassCoefficients(lowHz, options.sampleRate), lowpassCoefficients(highHz, options.sampleRate)], options.zeroPhase ?? true);
}
/**
* Removes mains interference at `frequency` and, optionally, its harmonics.
*
* Pick 50 or 60 Hz to match the local supply. Getting it wrong leaves the
* interference untouched while carving a hole in the spectrum somewhere useless.
*/
function notch(signal, frequency, options) {
	const nyquist = options.sampleRate / 2;
	const sections = [];
	for (let harmonic = 1; harmonic <= (options.harmonics ?? 1); harmonic++) {
		const centre = frequency * harmonic;
		if (centre >= nyquist) break;
		sections.push(notchCoefficients(centre, options.sampleRate, options.q ?? 30));
	}
	return apply(signal, sections, options.zeroPhase ?? true);
}
/** Subtracts the mean of each sample across channels — the common average reference. */
function commonAverageReference(channels) {
	const names = Object.keys(channels);
	if (names.length === 0) return {};
	const length = channels[names[0]].length;
	const out = {};
	for (const name of names) out[name] = new Float32Array(length);
	for (let i = 0; i < length; i++) {
		let sum = 0;
		for (const name of names) sum += channels[name][i];
		const mean = sum / names.length;
		for (const name of names) out[name][i] = channels[name][i] - mean;
	}
	return out;
}
/** Removes the mean. */
function detrend(signal) {
	const mean = signal.reduce((a, b) => a + b, 0) / (signal.length || 1);
	return signal.map((v) => v - mean);
}
/**
* Section Qs of an 8th-order Butterworth low-pass: `1 / (2·sin((2k−1)π/16))`.
*
* Cascading these four is maximally flat in the pass band and falls at 48 dB per
* octave — before the zero-phase pass doubles that.
*/
const BUTTERWORTH_8_Q = [
	.5098,
	.6013,
	.9,
	2.5629
];
/**
* Reduces the sample rate by an integer factor.
*
* Low-passes first, at 80 % of the new Nyquist frequency, with an 8th-order
* Butterworth. Dropping samples without that step folds everything above the new
* Nyquist back into the band as aliases, which look like real signal and cannot
* be removed afterwards. A single biquad once stood in for this filter and let
* through up to a quarter of an out-of-band tone's amplitude.
*/
function decimate(signal, factor, options) {
	if (!Number.isInteger(factor) || factor < 1) throw new RangeError(`Decimation factor must be a positive integer, got ${factor}`);
	if (factor === 1) return Float32Array.from(signal);
	const newNyquist = options.sampleRate / factor / 2;
	const filtered = apply(signal, BUTTERWORTH_8_Q.map((q) => lowpassCoefficients(newNyquist * .8, options.sampleRate, q)), options.zeroPhase ?? true);
	const out = new Float32Array(Math.floor(signal.length / factor));
	for (let i = 0; i < out.length; i++) out[i] = filtered[i * factor];
	return out;
}
//#endregion
//#region src/spectrum.ts
/**
* Spectral estimation.
*
* A single FFT of a whole recording gives a spectrum so noisy it is nearly
* useless — its variance does not fall as the record lengthens. Welch's method
* trades frequency resolution for stability by averaging over overlapping
* windows, which is what makes band power comparable between recordings.
*/
/** In-place radix-2 FFT. `real` and `imaginary` must have a power-of-two length. */
function fft(real, imaginary) {
	const n = real.length;
	if (n !== imaginary.length) throw new RangeError("FFT halves must be the same length");
	if (n === 0 || (n & n - 1) !== 0) throw new RangeError(`FFT length must be a power of two, got ${n}`);
	for (let i = 1, j = 0; i < n; i++) {
		let bit = n >> 1;
		for (; (j & bit) !== 0; bit >>= 1) j ^= bit;
		j ^= bit;
		if (i < j) {
			[real[i], real[j]] = [real[j], real[i]];
			[imaginary[i], imaginary[j]] = [imaginary[j], imaginary[i]];
		}
	}
	for (let length = 2; length <= n; length <<= 1) {
		const angle = -2 * Math.PI / length;
		const wReal = Math.cos(angle);
		const wImaginary = Math.sin(angle);
		for (let i = 0; i < n; i += length) {
			let curReal = 1;
			let curImaginary = 0;
			for (let j = 0; j < length / 2; j++) {
				const a = i + j;
				const b = a + length / 2;
				const tReal = real[b] * curReal - imaginary[b] * curImaginary;
				const tImaginary = real[b] * curImaginary + imaginary[b] * curReal;
				real[b] = real[a] - tReal;
				imaginary[b] = imaginary[a] - tImaginary;
				real[a] = real[a] + tReal;
				imaginary[a] = imaginary[a] + tImaginary;
				const nextReal = curReal * wReal - curImaginary * wImaginary;
				curImaginary = curReal * wImaginary + curImaginary * wReal;
				curReal = nextReal;
			}
		}
	}
}
/**
* Hann window: a good default trade-off between leakage and resolution.
*
* Symmetric (`/(length − 1)`), unlike SciPy's periodic default for Welch. A
* single-sample window is `[1]`; the formula would divide by zero there.
*/
function hannWindow(length) {
	const out = new Float64Array(length);
	if (length === 1) {
		out[0] = 1;
		return out;
	}
	for (let i = 0; i < length; i++) out[i] = .5 * (1 - Math.cos(2 * Math.PI * i / (length - 1)));
	return out;
}
/**
* Welch's averaged periodogram.
*
* Density is scaled so that integrating it over a frequency range gives the
* signal's power in that range — the property band power depends on, and the one
* a naive `|FFT|²` does not have.
*/
function welchPsd(signal, options) {
	const sampleRate = options.sampleRate;
	const segmentLength = options.segmentLength ?? 256;
	if (!Number.isInteger(segmentLength) || segmentLength < 4 || (segmentLength & segmentLength - 1) !== 0) throw new RangeError(`Segment length must be a power of two of at least 4, got ${segmentLength}`);
	const overlap = options.overlap ?? .5;
	if (overlap < 0 || overlap >= 1) throw new RangeError("Overlap must be within [0, 1)");
	const step = Math.max(1, Math.round(segmentLength * (1 - overlap)));
	const bins = segmentLength / 2 + 1;
	const density = new Float64Array(bins);
	const window = hannWindow(segmentLength);
	let windowPower = 0;
	for (const w of window) windowPower += w * w;
	const scale = 1 / (sampleRate * windowPower);
	let segments = 0;
	for (let start = 0; start + segmentLength <= signal.length; start += step) {
		const real = new Float64Array(segmentLength);
		const imaginary = new Float64Array(segmentLength);
		let mean = 0;
		for (let i = 0; i < segmentLength; i++) mean += signal[start + i];
		mean /= segmentLength;
		for (let i = 0; i < segmentLength; i++) real[i] = (signal[start + i] - mean) * window[i];
		fft(real, imaginary);
		for (let bin = 0; bin < bins; bin++) {
			const power = real[bin] ** 2 + imaginary[bin] ** 2;
			const folded = bin === 0 || bin === bins - 1 ? power : power * 2;
			density[bin] = density[bin] + folded * scale;
		}
		segments++;
	}
	if (segments > 0) for (let bin = 0; bin < bins; bin++) density[bin] = density[bin] / segments;
	else density.fill(NaN);
	const frequencies = new Float64Array(bins);
	for (let bin = 0; bin < bins; bin++) frequencies[bin] = bin * sampleRate / segmentLength;
	return {
		frequencies,
		density,
		sampleRate,
		segments,
		resolutionHz: sampleRate / segmentLength
	};
}
/** The canonical EEG frequency bands, in hertz. */
const EEG_BANDS = {
	delta: [1, 4],
	theta: [4, 8],
	alpha: [8, 13],
	beta: [13, 30],
	gamma: [30, 45]
};
/** Integrates the spectrum over a range, giving power in the signal's units². */
function bandPower(spectrum, fromHz, toHz) {
	let total = 0;
	for (let bin = 0; bin < spectrum.frequencies.length; bin++) {
		const frequency = spectrum.frequencies[bin];
		if (frequency >= fromHz && frequency < toHz) total += spectrum.density[bin];
	}
	return total * spectrum.resolutionHz;
}
/** Power in each canonical band. */
function bandPowers(spectrum) {
	const out = {};
	for (const [name, [from, to]] of Object.entries(EEG_BANDS)) out[name] = bandPower(spectrum, from, to);
	return out;
}
/**
* Each band's share of total band power.
*
* Usually the more comparable measure: absolute power depends on electrode
* contact and skull thickness, which differ between people and between sessions
* on the same person.
*/
function relativeBandPowers(spectrum) {
	const absolute = bandPowers(spectrum);
	const total = Object.values(absolute).reduce((a, b) => a + b, 0);
	if (total === 0) return absolute;
	const out = {};
	for (const [name, value] of Object.entries(absolute)) out[name] = value / total;
	return out;
}
//#endregion
//#region src/dominant.ts
/**
* Half-width of the median window, in bins.
*
* Three bins either side is wide enough that a rhythm two or three bins across
* stays a minority of the window — so the median tracks the background past it
* rather than following it — and narrow enough that the median still turns with
* a steeply falling spectrum.
*/
const MEDIAN_HALF_WIDTH = 3;
/**
* Bins analysed beyond each end of the search range.
*
* A peak needs a neighbour on each side and the smoothing needs one more, so
* without a margin the first and last two bins of the range could never be
* reported — a rhythm at 39-40 Hz came back as absent. The margin is analysed
* but a peak found in it is not reported.
*/
const EDGE_MARGIN_BINS = MEDIAN_HALF_WIDTH;
/**
* The strongest oscillation, found after removing the aperiodic background.
*
* That subtraction is the whole method, not a refinement. **EEG power falls off
* with frequency**, so the spectrum is a slide rather than a landscape, and
* taking its maximum reports the lowest frequency in the range for essentially
* every recording, awake or asleep. An earlier version of this function did
* exactly that and reported 1–2 Hz on every channel of an awake subject — which
* said nothing about the subject and everything about 1/f.
*
* Three things make it work, and each was needed:
*
* 1. **The background is the higher of a log-log line fit and a running
*    median.** Neither alone suffices. The line models the steep 1/f fall but
*    cannot bend around the knee real spectra have below a few hertz, leaving a
*    shoulder that outranks any true peak. The median follows any smooth shape
*    including the knee, but flattens out where the spectrum falls steeply.
*    Taking the higher of the two is the conservative choice in both regions.
* 2. **Only a local maximum counts.** A rhythm is a bump with the spectrum
*    falling away either side of it; a mis-modelled shoulder is monotonic.
* 3. **A prominence threshold.** Measured on pink and white noise across
*    several seeds, chance peaks reach 1.2–1.7 dB; a real rhythm clears 2.7 dB
*    at an amplitude where it is plainly visible in the trace.
*
* **Known limits.** Below about 8 Hz a rhythm must be markedly stronger to
* register, because the background is both steep and near its knee there. Delta
* and low-theta rhythms are therefore under-reported rather than over-reported,
* which is the safer direction for a device whose low frequencies are usually
* drift. At the other end the default range stops at 40 Hz to keep mains and an
* unidentified 42.7 Hz line out of the search — see `range`.
*/
function dominantFrequency(signal, sampleRate, options = {}) {
	const [low, requestedHigh] = options.range ?? [1, 40];
	const high = Math.min(requestedHigh, sampleRate / 2);
	if (!(low < high)) throw new RangeError(`Search range [${low}, ${requestedHigh}] is empty`);
	const threshold = options.minimumProminenceDb ?? 2.5;
	const mains = options.mainsHz === void 0 ? 50 : options.mainsHz;
	if (mains !== null && !(mains > 0)) throw new RangeError(`mainsHz must be positive or null, got ${mains}`);
	const excludeGrid = options.excludeSampleGridLines !== false;
	const segmentLength = 2 ** Math.floor(Math.log2(Math.min(signal.length, sampleRate)));
	if (segmentLength < 64) return null;
	const spectrum = welchPsd(detrend(signal), {
		sampleRate,
		segmentLength
	});
	const margin = EDGE_MARGIN_BINS * spectrum.resolutionHz;
	const hz = [];
	const logF = [];
	const logP = [];
	for (let bin = 0; bin < spectrum.frequencies.length; bin++) {
		const frequency = spectrum.frequencies[bin];
		if (frequency <= 0 || frequency < low - margin || frequency > high + margin) continue;
		const density = spectrum.density[bin];
		if (!(density > 0)) continue;
		hz.push(frequency);
		logF.push(Math.log10(frequency));
		logP.push(Math.log10(density));
	}
	const inRange = (frequency) => frequency >= low && frequency <= high;
	if (hz.length < 8 || hz.filter(inRange).length < 3) return null;
	const line = fitLine(logF, logP);
	const exponent = -line.slope;
	const background = logP.map((_, i) => {
		const from = Math.max(0, i - MEDIAN_HALF_WIDTH);
		const to = Math.min(logP.length - 1, i + MEDIAN_HALF_WIDTH);
		const window = logP.slice(from, to + 1).toSorted((a, b) => a - b);
		const median = window[window.length >> 1];
		return Math.max(line.intercept + line.slope * logF[i], median);
	});
	const residual = logP.map((value, i) => value - background[i]);
	const smoothed = residual.map((_, i) => {
		const from = Math.max(0, i - 1);
		const to = Math.min(residual.length - 1, i + 1);
		let sum = 0;
		for (let k = from; k <= to; k++) sum += residual[k];
		return sum / (to - from + 1);
	});
	const suspect = (frequency) => {
		if (mains !== null) {
			for (let harmonic = mains; harmonic <= high; harmonic += mains) if (Math.abs(frequency - harmonic) < .75) return true;
		}
		if (excludeGrid) {
			const fundamental = sampleRate / 12;
			for (let harmonic = fundamental; harmonic <= high; harmonic += fundamental) if (Math.abs(frequency - harmonic) < .75) return true;
		}
		return false;
	};
	let peakIndex = -1;
	let peakResidual = Number.NEGATIVE_INFINITY;
	for (let i = 1; i < smoothed.length - 1; i++) {
		const value = smoothed[i];
		if (value <= smoothed[i - 1] || value <= smoothed[i + 1]) continue;
		if (!inRange(hz[i]) || suspect(hz[i])) continue;
		if (value > peakResidual) {
			peakResidual = value;
			peakIndex = i;
		}
	}
	if (peakIndex < 0) return {
		hz: 0,
		band: null,
		prominenceDb: 0,
		clear: false,
		exponent
	};
	const prominenceDb = 10 * peakResidual;
	const frequency = hz[peakIndex];
	return {
		hz: frequency,
		band: bandOf(frequency),
		prominenceDb,
		clear: prominenceDb >= threshold,
		exponent
	};
}
/** Ordinary least squares, returning slope and intercept. */
function fitLine(xs, ys) {
	const n = xs.length;
	let sumX = 0;
	let sumY = 0;
	for (let i = 0; i < n; i++) {
		sumX += xs[i];
		sumY += ys[i];
	}
	const meanX = sumX / n;
	const meanY = sumY / n;
	let covariance = 0;
	let variance = 0;
	for (let i = 0; i < n; i++) {
		const dx = xs[i] - meanX;
		covariance += dx * (ys[i] - meanY);
		variance += dx * dx;
	}
	const slope = variance === 0 ? 0 : covariance / variance;
	return {
		slope,
		intercept: meanY - slope * meanX
	};
}
/** Which band a frequency falls in, or `null` outside the defined bands. */
function bandOf(hz) {
	for (const [name, [bandLow, bandHigh]] of Object.entries(EEG_BANDS)) if (hz >= bandLow && hz < bandHigh) return name;
	return null;
}
//#endregion
//#region src/epoch.ts
/**
* Concatenates chunks into one continuous signal.
*
* Chunks must come from a single sensor and be in order. Gaps are not filled:
* where the device dropped samples the timestamps simply jump, and the caller
* can see that in the timestamps rather than being handed silent interpolation.
*/
function concatenate(chunks) {
	if (chunks.length === 0) return {
		channels: {},
		timestamps: /* @__PURE__ */ new Float64Array(0),
		sampleRate: 0
	};
	const first = chunks[0];
	const names = first.channelNames;
	let total = 0;
	for (const chunk of chunks) total += chunk.timestamps.length;
	const channels = {};
	for (const name of names) channels[name] = new Float32Array(total);
	const timestamps = new Float64Array(total);
	let offset = 0;
	for (const chunk of chunks) {
		timestamps.set(chunk.timestamps, offset);
		for (const name of names) {
			const column = chunk.channels[name];
			if (column !== void 0) channels[name].set(column, offset);
		}
		offset += chunk.timestamps.length;
	}
	return {
		channels,
		timestamps,
		sampleRate: first.sampleRate
	};
}
/**
* Cuts a window around each event.
*
* Events falling too near either end of the recording are skipped rather than
* padded: a half-length epoch averaged with full ones distorts the result, and
* silently zero-padding one would be worse. So are events whose window spans a
* gap in the data: slicing by sample index once let an epoch run straight across
* dropped samples, so its "after the event" half began at the wrong time.
*/
function epoch(signal, events, options) {
	const { timestamps, sampleRate } = signal;
	if (timestamps.length === 0 || sampleRate === 0) return [];
	const beforeSamples = Math.round(options.before * sampleRate);
	const length = beforeSamples + Math.round(options.after * sampleRate);
	const names = Object.keys(signal.channels);
	const out = [];
	const maxStepUs = 15e5 / sampleRate;
	for (const event of events) {
		const centre = indexAtOrAfter(timestamps, event.timestamp);
		if (centre < 0) continue;
		const start = centre - beforeSamples;
		if (start < 0 || start + length > timestamps.length) continue;
		if (spansGap(timestamps, start, start + length, maxStepUs)) continue;
		const channels = {};
		for (const name of names) {
			const slice = signal.channels[name].slice(start, start + length);
			if (options.baselineCorrect !== false && beforeSamples > 0) {
				let baseline = 0;
				for (let i = 0; i < beforeSamples; i++) baseline += slice[i];
				baseline /= beforeSamples;
				for (let i = 0; i < slice.length; i++) slice[i] = slice[i] - baseline;
			}
			channels[name] = slice;
		}
		out.push({
			label: event.label,
			eventTime: event.timestamp,
			channels,
			timestamps: timestamps.slice(start, start + length),
			sampleRate,
			startOffsetSeconds: (timestamps[start] - event.timestamp) / 1e6
		});
	}
	return out;
}
/**
* Averages epochs sample by sample, the usual way to recover an evoked response.
*
* @throws {RangeError} when the epochs differ in length or sample rate. Epochs
* cut by one `epoch()` call never do; mixing calls is a mistake, and quietly
* averaging only the matching ones — as this once did, while labelling the result
* with the full count — hid it.
*/
function averageEpochs(epochs) {
	if (epochs.length === 0) return null;
	const first = epochs[0];
	const names = Object.keys(first.channels);
	const length = first.timestamps.length;
	for (const item of epochs) if (item.timestamps.length !== length || item.sampleRate !== first.sampleRate) throw new RangeError(`Cannot average epochs of different shapes: ${item.timestamps.length} samples at ${item.sampleRate} Hz against ${length} at ${first.sampleRate} Hz`);
	const channels = {};
	for (const name of names) {
		const sum = new Float32Array(length);
		let counted = 0;
		for (const item of epochs) {
			const column = item.channels[name];
			if (column === void 0 || column.length !== length) continue;
			for (let i = 0; i < length; i++) sum[i] = sum[i] + column[i];
			counted++;
		}
		if (counted > 0) for (let i = 0; i < length; i++) sum[i] = sum[i] / counted;
		channels[name] = sum;
	}
	return {
		label: `${first.label} (mean of ${epochs.length})`,
		eventTime: first.eventTime,
		channels,
		timestamps: first.timestamps,
		sampleRate: first.sampleRate,
		startOffsetSeconds: first.startOffsetSeconds
	};
}
function spansGap(timestamps, from, to, maxStepUs) {
	for (let i = from + 1; i < to; i++) if (timestamps[i] - timestamps[i - 1] > maxStepUs) return true;
	return false;
}
/** First index whose timestamp is at or after `target`, or -1. */
function indexAtOrAfter(timestamps, target) {
	let low = 0;
	let high = timestamps.length - 1;
	let found = -1;
	while (low <= high) {
		const mid = low + high >> 1;
		if (timestamps[mid] >= target) {
			found = mid;
			high = mid - 1;
		} else low = mid + 1;
	}
	return found;
}
//#endregion
export { BiquadFilter, EEG_BANDS, averageEpochs, bandOf, bandPower, bandPowers, bandpass, commonAverageReference, concatenate, decimate, detrend, dominantFrequency, epoch, fft, hannWindow, highpass, highpassCoefficients, lowpass, lowpassCoefficients, notch, notchCoefficients, relativeBandPowers, welchPsd };

//# sourceMappingURL=index.js.map